import { useCallback, useEffect, useMemo, useState } from 'react';

import type { EditorActions } from '@atlaskit/editor-core/actions';

import type {
	SearchExtensionActions,
	SearchExtensionParameters,
	UpdateField,
	UseSearchExtensionParametersAndActionsProps,
	UseSearchExtensionParametersAndActionsResponse,
} from '../../types';

const updateNodeParameters = (editorActions: EditorActions, updatedParameters: any) => {
	const selectedNodeJSON = editorActions.getSelectedNode()?.toJSON();

	const updatedNodeSelection = {
		...selectedNodeJSON,
		attrs: {
			...selectedNodeJSON?.attrs,
			parameters: { ...updatedParameters },
		},
	};

	editorActions.replaceSelection(updatedNodeSelection, true);
};

const fetchLocalIdMacroParameters = (
	editorActions: EditorActions,
	localId: string,
): SearchExtensionParameters => {
	const localIdNode = editorActions?.getNodeByLocalId?.(localId)?.toJSON();
	let params = localIdNode?.attrs?.parameters;

	if (params === undefined) {
		return undefined as any;
	}

	// Make a copy of the params object to avoid mutating the original object
	params = { ...params };

	return params;
};

const removeProperties = (
	extensionsPropertiesToRemove: string[],
	extensionParameters: SearchExtensionParameters,
): SearchExtensionParameters => {
	const mutatedParameters = { ...extensionParameters };

	extensionsPropertiesToRemove.forEach((field) => {
		if (field in mutatedParameters) {
			delete mutatedParameters[field as keyof typeof mutatedParameters];
		}
	});

	return mutatedParameters;
};

export const useSearchParametersAndActions = ({
	localId,
	editorActions,
}: UseSearchExtensionParametersAndActionsProps): UseSearchExtensionParametersAndActionsResponse => {
	const [extensionParameters, setExtensionParameters] = useState<SearchExtensionParameters>(
		fetchLocalIdMacroParameters(editorActions, localId),
	);

	useEffect(() => {
		const extensionNodeParams = fetchLocalIdMacroParameters(editorActions, localId);
		setExtensionParameters(extensionNodeParams);
	}, [editorActions, localId]);

	const updateExtensionParams = useCallback(
		(
			newExtensionParameters: UpdateField<SearchExtensionParameters>,
			options: { fieldsToRemove?: string[] } = {},
		) => {
			setExtensionParameters((prevParams) => {
				let mutatedFields = { ...prevParams };
				const fieldsToRemove = [...(options?.fieldsToRemove || [])];

				if (fieldsToRemove.length > 0) {
					mutatedFields = removeProperties(fieldsToRemove, prevParams);
				}

				const updatedParams = {
					...mutatedFields,
					...newExtensionParameters,
				} as SearchExtensionParameters;

				// Save params to ADF and state
				updateNodeParameters(editorActions, updatedParams);

				return updatedParams;
			});
		},
		[editorActions],
	);

	const updateMainFormField = useCallback(
		(updatedFields: UpdateField<SearchExtensionParameters>, options = {}) =>
			updateExtensionParams(updatedFields, options),
		[updateExtensionParams],
	);

	const extensionActions: SearchExtensionActions = useMemo(
		() => ({
			updateMainFormField,
		}),
		[updateMainFormField],
	);

	return { extensionParameters, extensionActions };
};
